/**
 * 새 계약 오류 코드(SUPPORT_ERROR_CODES·PEP_ERROR_CODES) → 화면 문구·다음 동작 (W6 E2).
 * 문구는 네 관리 앱이 같다(SUPPORT 9 화면 약속에 문구가 있는 것은 그 문구). 같은 key 로 한 번만 띄운다.
 * Vue·axios 를 들이지 않는 순수 모듈이라 node --test 로 시험한다(`__tests__/standardErrors.test.mjs`).
 */

// 오류 본문: 계약 {statusCode, code, reason?, message} 또는 call-api 봉투 {error: {code, …}}
const bodyOf = (error) => {
  const d = error?.response?.data || {};
  return !d.code && d.error && typeof d.error === "object" ? d.error : d;
};
const status = (error) => error?.response?.status;

/** 401 SUPPORT_SESSION_ENDED {reason} 의 사유 문구(계약 SUPPORT_END_REASONS) */
export const SUPPORT_END_REASON_TEXT = {
  NO_SESSION: "지원 접속 기록이 없습니다",
  MISMATCH: "지원 접속 정보가 맞지 않습니다",
  ENDED: "종료했습니다",
  EXPIRED: "최대 8시간이 지났습니다",
  IDLE: "30분 동안 쓰지 않았습니다",
  CONSENT_OFF: "고객사가 동의를 껐습니다",
  RESELLER_UNLINKED: "리셀러 연동이 끊겼습니다",
  SUB_ACCOUNT_LOCKED: "지원 계정이 잠겼습니다",
  ACTOR_INACTIVE: "접속한 사람의 계정이 정지되었습니다",
  ACTOR_NO_PERMISSION: "접속한 사람에게 지원 권한이 없습니다",
  SUPPORT_ACCESS_OFF: "지원 접속이 꺼졌습니다",
  INTROSPECT_UNAVAILABLE: "보안 확인 서버에 연결하지 못했습니다",
};

export const supportEndedText = (reason) =>
  `지원 접속이 끝났습니다(${SUPPORT_END_REASON_TEXT[reason] || "사유 알 수 없음"}).`;

export const STANDARD_ERROR_TEXT = {
  SUPPORT_SCOPE_DENIED: "지원 접속에서는 쓸 수 없는 메뉴입니다",
  SUPPORT_ACCOUNT_READONLY: "서티라이프(리셀러) 지원 계정은 고치거나 지울 수 없습니다.",
  SUPPORT_ACCOUNT_LOGIN_DENIED: "지원 계정은 이 화면으로 로그인할 수 없습니다.",
  ACCOUNT_INACTIVE: "계정이 정지되었거나 삭제되었습니다.",
  PERM_UNAVAILABLE: "잠시 후 다시 시도해 주세요.",
  AUDIT_UNAVAILABLE: "기록 장치 문제로 이 작업을 하지 않았습니다. 잠시 후 다시 시도해 주세요.",
  AUDIT_QUERY_TOO_BROAD: "조건을 좁혀 주세요(기간·회사).",
  AUDIT_BUSY: "잠시 후 다시 시도해 주세요.",
  MFA_ENROLL_REQUIRED: "이 고객사는 2차 인증 필수입니다. 「내 계정 보안」에서 수단을 먼저 등록하세요.",
  OPERATOR_ACTING_DENIED: "운영 도구 경로가 아닙니다. 고객 화면은 「지원 계정으로 접속」으로 보세요.",
  SUPPORT_CONSENT_REQUIRED: "고객사 동의가 없습니다.",
  SUPPORT_SUB_ACCOUNT_LOCKED: "지원 계정이 잠겨 있습니다.",
  SUPPORT_ACCESS_OFF: "지원 접속이 아직 켜지지 않았습니다(보안 설정).",
  SUPPORT_PROOF_REQUIRED: "본인 확인이 필요합니다.",
  SUPPORT_CODE_INVALID: "접속 코드가 만료되었거나 이미 쓰였습니다. 다시 눌러 주세요.",
};

/** 403 PERMISSION_DENIED 문구. 이름표(code → ko)가 없으면 코드 그대로 */
export const permissionDeniedText = (required, labels = {}) => {
  const list = (Array.isArray(required) ? required : required ? [required] : []).map(
    (c) => labels[c] || c
  );
  return list.length ? `권한이 없습니다(필요: ${list.join(", ")})` : "권한이 없습니다.";
};

/** 주소로 연, 메뉴에 안 보이는 화면 → 첫 화면으로 옮길 때 알림(REDIRECT-REQ 4) */
export const ROUTE_DENIED_TEXT = "이 메뉴를 볼 권한이 없습니다. 첫 화면으로 이동했어요.";
/** 이름표가 있는 코드만 「필요:」에 넣는다(영문 코드 노출 금지). 하나도 없으면 ROUTE_DENIED_TEXT */
export const routeDeniedText = (codes = [], labels = {}) => {
  const named = codes.filter((c) => labels[c]);
  return named.length ? permissionDeniedText(named, labels) : ROUTE_DENIED_TEXT;
};

/**
 * 오류 응답 → 알림 하나. 처리할 코드가 아니면 null.
 * @returns {{ code, key, level: 'warning'|'error', text, action?: 'LOGOUT'|'RELOAD_PERMS'|'SUPPORT_ENDED', required? }|null}
 *   action: LOGOUT = 저장값 지우고 로그인 화면 · RELOAD_PERMS = introspect 를 한 번만 다시 읽고 메뉴 다시 그림
 *           SUPPORT_ENDED = (지원 탭) 갱신하지 않고 certi.support.* 를 지운 뒤 끝 화면
 * @param ctx.supportTab 지원 탭이면 true(병원·제조사 앱만 — 운영자 콘솔은 늘 false)
 * @param ctx.labels 권한 이름표 {code: ko}
 */
export const standardErrorNotice = (error, ctx = {}) => {
  const s = status(error);
  const data = bodyOf(error);
  const c = data.code;
  const url = String(error?.config?.url || "");
  const make = (level, text, extra = {}) => ({ code: c, key: c || `HTTP_${s}`, level, text, ...extra });

  if (s === 401) {
    if (c === "SUPPORT_SESSION_ENDED" && ctx.supportTab)
      return make("error", supportEndedText(data.reason), { action: "SUPPORT_ENDED" });
    if (c === "ACCOUNT_INACTIVE") return make("error", STANDARD_ERROR_TEXT.ACCOUNT_INACTIVE, { action: "LOGOUT" });
    if (c === "SUPPORT_ACCOUNT_LOGIN_DENIED" || c === "SUPPORT_CODE_INVALID")
      return make("warning", STANDARD_ERROR_TEXT[c]);
    return null;
  }
  if (s === 403) {
    if (c === "PERMISSION_DENIED" || c === "PERM_UNMARKED")
      return make("warning", permissionDeniedText(data.required, ctx.labels), {
        action: "RELOAD_PERMS",
        required: data.required,
      });
    if (
      [
        "SUPPORT_SCOPE_DENIED",
        "SUPPORT_ACCOUNT_READONLY",
        "MFA_ENROLL_REQUIRED",
        "OPERATOR_ACTING_DENIED",
        "SUPPORT_CONSENT_REQUIRED",
        "SUPPORT_SUB_ACCOUNT_LOCKED",
        "SUPPORT_ACCESS_OFF",
        "SUPPORT_PROOF_REQUIRED",
      ].includes(c)
    )
      return make("warning", STANDARD_ERROR_TEXT[c]);
    return null;
  }
  if (s === 503 && (c === "PERM_UNAVAILABLE" || c === "AUDIT_UNAVAILABLE"))
    return make("error", STANDARD_ERROR_TEXT[c]);
  if (s === 400 && c === "AUDIT_QUERY_TOO_BROAD") return make("warning", STANDARD_ERROR_TEXT.AUDIT_QUERY_TOO_BROAD);
  // 이용 내역 조회는 프로세스당 동시 2개(429) — 그 밖의 429(인증번호 요청 등)는 각 화면이 처리
  if (s === 429 && url.includes("/audit/")) return { ...make("warning", STANDARD_ERROR_TEXT.AUDIT_BUSY), key: "AUDIT_BUSY" };
  return null;
};

/**
 * fn 을 ms 안에 한 번만 부른다(그 사이 부른 것은 같은 결과를 기다림). PERMISSION_DENIED 마다 introspect 를
 * 다시 읽으면 반복되므로 이것으로 막는다.
 */
export function createOnceWithin(fn, ms, now = () => Date.now()) {
  let last = -Infinity;
  let pending = null;
  return () => {
    if (pending) return pending;
    if (now() - last < ms) return Promise.resolve(false);
    last = now();
    pending = Promise.resolve()
      .then(fn)
      .then(
        () => true,
        () => false
      )
      .finally(() => {
        pending = null;
      });
    return pending;
  };
}
